import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import { getTemplatePermissions, getProjectMembers } from '../utils/api';
import styles from './TemplatePermissionsModal.module.css';

// Per-template permissions editor for admin/PM. Loads the current allow-list
// + project members list, lets the writer toggle role-wide grants and
// individual user grants, and PUT-replaces the whole list on Save.
//
// The page itself shows admin/pm badges as "always allowed" (read-only,
// disabled checkboxes) so it's clear they don't need to be added.

// Note: with the post-roles-refactor model, admin / pm / super already pass
// the fill check unconditionally — Super was carved out of the old `field`
// role specifically so daily-report-style fills don't need allow-listing.
// `field` is strict read-only and cannot fill regardless of allow-list,
// so we don't surface it as a role-level allow-list option here.
const ROLES = [
  { value: 'admin', label: 'Admin',           implicit: true, blurb: 'Always allowed (cannot be removed)' },
  { value: 'pm',    label: 'Project Manager', implicit: true, blurb: 'Always allowed (cannot be removed)' },
  { value: 'super', label: 'Super',           implicit: true, blurb: 'Always allowed (cannot be removed)' },
];

export default function TemplatePermissionsModal({ template, onClose, onSave }) {
  const { project } = useStore();
  const [members, setMembers]       = useState([]);
  const [grantedRoles, setGrantedRoles] = useState(new Set());
  const [grantedUsers, setGrantedUsers] = useState(new Set());
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);

  // Load: project members + current permissions in parallel
  useEffect(() => {
    let alive = true;
    Promise.all([
      getProjectMembers(project.id),
      getTemplatePermissions(template.id),
    ]).then(([memberList, perms]) => {
      if (!alive) return;
      setMembers(memberList);
      const roles = new Set(perms.filter((p) => p.role).map((p) => p.role));
      const users = new Set(perms.filter((p) => p.user_id).map((p) => p.user_id));
      setGrantedRoles(roles);
      setGrantedUsers(users);
      setLoading(false);
    }).catch((err) => {
      toast.error(err.message);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [project.id, template.id]);

  function toggleRole(r) {
    setGrantedRoles((s) => {
      const next = new Set(s);
      if (next.has(r)) next.delete(r); else next.add(r);
      return next;
    });
  }
  function toggleUser(uid) {
    setGrantedUsers((s) => {
      const next = new Set(s);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSave({
        roles: Array.from(grantedRoles),
        user_ids: Array.from(grantedUsers),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span>Permissions · {template.name}</span>
          <button className={styles.closeBtn} onClick={onClose} disabled={saving}>×</button>
        </div>

        <div className={styles.body}>
          <p className={styles.blurb}>
            Pick who can fill this form. Admin and PM are always allowed.
            Anyone else needs either their role granted or to be picked
            individually below.
          </p>

          {loading ? <div className={styles.empty}>Loading…</div> : (
            <>
              <section className={styles.section}>
                <div className={styles.sectionLabel}>By role</div>
                <div className={styles.roleGrid}>
                  {ROLES.map((r) => {
                    const granted = r.implicit || grantedRoles.has(r.value);
                    return (
                      <label
                        key={r.value}
                        className={`${styles.row} ${granted ? styles.rowActive : ''} ${r.implicit ? styles.rowImplicit : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={granted}
                          disabled={r.implicit}
                          onChange={() => !r.implicit && toggleRole(r.value)}
                        />
                        <div className={styles.rowText}>
                          <span className={styles.rowName}>{r.label}</span>
                          <span className={styles.rowSub}>{r.blurb}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionLabel}>Individual project members</div>
                {members.length === 0 ? (
                  <div className={styles.empty}>No project members.</div>
                ) : (
                  <div className={styles.memberGrid}>
                    {members.map((m) => {
                      // If their role is granted as a role, individual checkbox
                      // is shown but visually marked as "covered by role" so the
                      // admin doesn't get confused why the member can fill.
                      const coveredByRole =
                        grantedRoles.has(m.role) || m.role === 'admin' || m.role === 'pm';
                      const checked = grantedUsers.has(m.id);
                      return (
                        <label
                          key={m.id}
                          className={`${styles.row} ${(checked || coveredByRole) ? styles.rowActive : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={coveredByRole && !checked}
                            onChange={() => toggleUser(m.id)}
                          />
                          <div className={styles.rowText}>
                            <span className={styles.rowName}>{m.name}</span>
                            <span className={styles.rowSub}>
                              {m.email} · {m.role}
                              {coveredByRole && !checked && ' — covered by role'}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
